/**
 *  This component is used to get user's personal details for credit card Application
 *
 * @module views/components/CreditCardPersonalInfo
 * @memberof -Common
 */
import './CreditCardPersonalInfo.scss';

import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import InputField from '@ulta/core/components/InputField/InputField';
import Text from '@ulta/core/components/Text/Text';
import constants from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import Button from '@ulta/modules/Button/Button';

import AddressForm from '../AddressForm/AddressForm';
import MetadataList from '../MetadataList/MetadataList';
import * as utils from './CreditCardPersonalInfo';

/**
 * Represents a CreditCardPersonalInfo component
 *
 * @method
 * @param {CreditCardPersonalInfoFormProps} props - React properties passed from composition
 * @returns CreditCardPersonalInfo
 */
export const CreditCardPersonalInfoForm = function( props ){
  const { handleBlur, showAddress2, personalInfoTitle, personalInfoSubText } = props;
  const { firstName, lastName, phoneNumber, address } = utils.decorateFormFields( props );

  return (
    <div className='CreditCardPersonalInfoForm'>
      { personalInfoTitle &&
      <div className={ classNames( 'CreditCardPersonalInfoForm__title', {
        'CreditCardPersonalInfoForm__title--noMargin': !!personalInfoSubText
      } ) }
      >
        <Text
          htmlTag='h2'
          textStyle='title-6'
        >
          { personalInfoTitle }
        </Text>
      </div> }
      {
        personalInfoSubText && <div className='CreditCardPersonalInfoForm__subTitle'>
          <Text
            htmlTag='p'
            textStyle='body-2'
            color='neutral-600'
          >
            { personalInfoSubText }
          </Text>
        </div>
      }
      <div className='CreditCardPersonalInfoForm__contentWrapper'>
        { firstName &&
        <InputField
          { ...spreadProps( { ...firstName } ) }
          handleBlur={ handleBlur }
          autoComplete='given-name'
        /> }
        { lastName &&
        <InputField
          { ...spreadProps( { ...lastName } ) }
          handleBlur={ handleBlur }
          autoComplete='family-name'
        /> }
        { address && (
          <AddressForm
            { ...spreadProps( { ...address } ) }
            addressLine2Label={ address.addressLineTwoLabel }
            showAddress2ByDefault={ showAddress2 }
          />
        ) }
        { phoneNumber && (
          <InputField
            { ...spreadProps( { ...phoneNumber } ) }
            handleBlur={ handleBlur }
            formatter={ constants?.FORMATTER_PATTERNS.PHONE_NO_FORMATTER }
            prefersRawValue={ true }
            autoFormat='tel'
          /> ) }
      </div>
    </div>
  );
};

/**
 * @method decorateFormFields
 * @param {*} data
 * @property {object} data.firstName - form prop, firstName input field
 * @property {object} data.lastName - form prop, lastName input field
 * @property {object} data.phoneNumber - form prop, Set the phoneNumber input field
 * @property {object} data.address - form prop, Set the address input fields
 * @returns {object} with data related to inputs
 */
export const decorateFormFields = ( data ) => {
  const { firstName, lastName, phoneNumber, formDisabled, ...address } = data || {};

  if( formDisabled ){
    firstName.enabled = false;
    lastName.enabled = false;
    phoneNumber.enabled = false;

    if( address?.address1 ){
      address.address1.enabled = false;
      address.address2.enabled = false;
      address.city.enabled = false;
      address.postalCode.enabled = false;
      address.state.enabled = false;
    }
  }

  return { firstName, lastName, phoneNumber, address };
};

/**
 * Property type definitions
 * @typedef CreditCardPersonalInfoFormProps
 * @type {object}
 * @property {object} address1 - Set the address input field
 * @property {object} address2 - Set the address2 input field
 * @property {string} addressLineTwoLabel - Set the label value for address2
 * @property {object} city - Set the city input field
 * @property {string} personalInfoTitle - Set the personal info section title
 * @property {object} firstName - Set the firstName input field
 * @property {function} handleBlur - Set handleBlur function
 * @property {object} lastName - Set the lastName input field
 * @property {object} personalInfoTitle - Set the title for the Personal Info Form
 * @property {object} phoneNumber - Set the phoneNumber input field
 * @property {object} postalCode - Set the postal code input field
 * @property {object} state - Set the state select field
 */
export const CreditCardPersonalInfoFormProps =  {
  personalInfoTitle: PropTypes.string,
  firstName: PropTypes.object,
  lastName: PropTypes.object,
  phoneNumber: PropTypes.object
};

CreditCardPersonalInfoForm.propTypes = CreditCardPersonalInfoFormProps;

export const PersonalDetailsReadOnly = props => {
  const {
    address1,
    address2,
    addressLabel,
    city,
    componentKey,
    editAction,
    editHelpLabel,
    email,
    emailLabel,
    firstName,
    lastName,
    nameLabel,
    onEdit,
    phoneNumber,
    phoneNumberLabel,
    postalCode,
    state,
    title
  } = props;

  const personalDetailsMetadata = {
    [nameLabel]: `${firstName} ${lastName}`,
    [emailLabel]: email,
    [addressLabel]: `${address1}\n${address2 ? `${address2}\n` : ''}${city}, ${state} ${postalCode}`,
    [phoneNumberLabel]: phoneNumber
  };

  return (
    <div className='ReadOnlyPersonalDetails'
      role='region'
      aria-labelledby='personalDetailsInfo'
    >
      <div className='ReadOnlyPersonalDetails__HeaderContainer'
        id='personalDetailsInfo'
      >
        { title && (
          <Text
            htmlTag='h2'
            textStyle='title-6'
          >
            { title }
          </Text>
        ) }
        { editHelpLabel && editAction && (
          <div className='ReadOnlyPersonalDetails__EditContainer'>
            <Text
              htmlTag='p'
              textStyle='body-2'
            >
              { editHelpLabel }
            </Text>
            <Button
              action={ editAction }
              ariaHiddenIcon
              ariaLabel={ editAction.label }
              iconImage='Edit'
              iconLabel={ editAction.label }
              iconRight
              label={ editAction.label }
              onClick={ onEdit }
              tertiary
              tiny
            />
          </div>
        ) }
      </div>
      { title && personalDetailsMetadata && (
        <MetadataList
          containerClassName='ReadOnlyPersonalDetails__MetadataListContainer'
          componentKey={ `MetadataList-${ componentKey }` }
          data={ Object.entries( personalDetailsMetadata ) }
          labelColor='neutral-600'
          labelTextStyle='eyebrow'
          stacked
          title={ title }
          valueColor='black'
          valueTextStyle='body-2'
        />
      ) }
    </div>
  );
};

export const PersonalDetailsReadOnlyProps = {
  address1: PropTypes.string,
  address2: PropTypes.string,
  addressLabel: PropTypes.string,
  city: PropTypes.string,
  componentKey: PropTypes.string,
  editAction: PropTypes.object,
  editHelpLabel: PropTypes.string,
  email: PropTypes.string,
  emailLabel: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  nameLabel: PropTypes.string,
  onEdit: PropTypes.func,
  phoneNumber: PropTypes.string,
  phoneNumberLabel: PropTypes.string,
  postalCode: PropTypes.string,
  setIsModified: PropTypes.func,
  state: PropTypes.string,
  title: PropTypes.string
};

PersonalDetailsReadOnly.propTypes = PersonalDetailsReadOnlyProps;

const CreditCardPersonalInfo = props => {
  const {
    componentKey,
    handleBlur,
    personalDetailsLegalText,
    prefilledPersonalDetails = {},
    setIsModified,
    showPersonalDetailsExpanded,
    ...personalInfoForm
  } = props;

  const [isEditing, setIsEditing] = useState( !!showPersonalDetailsExpanded );

  useEffect( () => {
    handleOnChangeOfShowPersonalDetailsExpanded( { showPersonalDetailsExpanded }, { setIsEditing } );
  }, [showPersonalDetailsExpanded] );

  const showPersonalDetailsAsReadOnly = !isEditing;

  const PersonalDetailsComponent = showPersonalDetailsAsReadOnly ? (
    <PersonalDetailsReadOnly
      { ...spreadProps( { ...prefilledPersonalDetails } ) }
      componentKey={ componentKey }
      onEdit={ () => utils.handleEditClick( { setIsEditing, setIsModified } ) }
      setIsModified={ setIsModified }
    />
  ) : (
    <CreditCardPersonalInfoForm
      { ...spreadProps( { ...personalInfoForm } ) }
      handleBlur={ handleBlur }
      required={ true }
      showAddress2={ prefilledPersonalDetails?.address2 }
    />
  );

  return (
    <div>
      { PersonalDetailsComponent }
      { personalDetailsLegalText && (
        <Text
          textStyle='body-2'
          htmlTag='p'
          color='neutral-600'
        >
          { personalDetailsLegalText }
        </Text>
      ) }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef CreditCardPersonalInfoProps
 * @type {object}
 * @property {object} address1 - Set the address input field
 * @property {object} address2 - Set the address2 input field
 * @property {string} addressLineTwoLabel - Set the label value for address2
 * @property {object} city - Set the city input field
 * @property {object} firstName - Set the firstName input field
 * @property {function} handleBlur - Set handleBlur function
 * @property {object} lastName - Set the lastName input field
 * @property {string} personalDetailsLegalText - Set the personalDetailsLegalText
 * @property {object} personalInfoTitle - Set the title for the Personal Info Form
 * @property {object} phoneNumber - Set the phoneNumber input field
 * @property {object} postalCode - Set the postal code input field
 * @property {object} prefilledPersonalDetails - Displays read only personal information
 * @property {function} setIsModified - Set form field flag for isModified
 * @property {boolean} showPersonalDetailsExpanded - Flag to show personal details form (edit view)
 * @property {object} state - Set the state select field
 */
export const CreditCardPersonalInfoProps = {
  ...CreditCardPersonalInfoFormProps,
  prefilledPersonalDetails: PropTypes.shape( PersonalDetailsReadOnlyProps ),
  showPersonalDetailsExpanded: PropTypes.bool
};

CreditCardPersonalInfo.propTypes = { ...CreditCardPersonalInfoProps };

export default CreditCardPersonalInfo;

/**
* A click handler function for clicking Edit button in read only personal information
* @method
* @param {Object} methods - Passing methods as an argument for the mutation call
*/
export const handleEditClick = ( methods ) => {
  const { setIsEditing, setIsModified } = handleEmptyObjects( methods );

  setIsEditing( true );
  setIsModified( true );
};

/**
* Handler function for to update isEditing when showPersonalDetailsExpanded changes (such as during SignOn)
* @method
* @param {Object} data - Passing required data as an argument
* @param {Object} methods - Passing methods as an argument for the mutation call
*/
export const handleOnChangeOfShowPersonalDetailsExpanded = ( data, methods ) => {
  const { showPersonalDetailsExpanded } = handleEmptyObjects( data );
  const { setIsEditing } = handleEmptyObjects( methods );
  const hasValue = showPersonalDetailsExpanded !== null && showPersonalDetailsExpanded !== undefined;

  if( setIsEditing && hasValue ){
    setIsEditing( !!showPersonalDetailsExpanded );
  }
};